import { useState } from 'react';
import { PlanProvider } from './store/planStore.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { mondayOf, useHistoryNav, type View } from './nav/useHistoryNav.js';
import { addDays, isoDate } from '../domain/calendar.js';
import { PlanWeek } from './screens/PlanWeek.js';
import { MealPicker } from './screens/MealPicker.js';
import { Lunches } from './screens/Lunches.js';
import { Staples } from './screens/Staples.js';
import { ChooseStore } from './screens/ChooseStore.js';
import { CupboardCheck } from './screens/CupboardCheck.js';
import { ShoppingList } from './screens/ShoppingList.js';
import { AfterTheShop } from './screens/AfterTheShop.js';
import { Settings } from './screens/Settings.js';
import { SharingProvider } from './sync/SharingProvider.js';
import { AdoptDialog, SharingBanner } from './components/Sharing.js';

/**
 * Today's Monday. Read once at mount: a shopping trip must not silently jump
 * to a different week because midnight passed while the app was open.
 */
function thisMonday(): string {
  return mondayOf(isoDate(new Date().toISOString().slice(0, 10)));
}

export function App(): React.JSX.Element {
  const [weekStart, setWeekStart] = useState<string>(thisMonday);
  const { view, go, back } = useHistoryNav<View>('plan');

  const shiftWeek = (weeks: number): void => {
    setWeekStart((current) => addDays(isoDate(current), weeks * 7));
  };

  return (
    <PlanProvider weekStart={weekStart}>
      <SharingProvider weekStart={weekStart}>
        <div className="app">
          <ErrorBoundary resetKey={view}>
            <Screen view={view} go={go} back={back} shiftWeek={shiftWeek} />
          </ErrorBoundary>
          <TabBar view={view} go={go} />
        </div>
        {/*
          Outside the ErrorBoundary and outside every screen: the first-run
          question must survive a tab change and a screen that throws, because
          it is the one moment where guessing would destroy somebody's plan.
        */}
        <AdoptDialog />
      </SharingProvider>
    </PlanProvider>
  );
}

function Screen({
  view,
  go,
  shiftWeek,
}: {
  view: View;
  go: (v: View) => void;
  back: () => void;
  shiftWeek: (weeks: number) => void;
}): React.JSX.Element {
  const [picking, setPicking] = useState<string | null>(null);

  return (
    <>
      <main data-view={view}>
        {view === 'plan' ? (
          <>
            <SharingBanner onOpenSettings={() => go('settings')} />
            <AfterTheShop today={isoDate(new Date().toISOString().slice(0, 10))} />
            <WeekStepper shiftWeek={shiftWeek} />
            <PlanWeek onPickMeal={setPicking} />
          </>
        ) : view === 'lunches' ? (
          <Lunches onDone={() => go('plan')} />
        ) : view === 'staples' ? (
          <Staples onDone={() => go('plan')} />
        ) : view === 'store' ? (
          <ChooseStore onChosen={() => go('cupboard')} />
        ) : view === 'cupboard' ? (
          <CupboardCheck onDone={() => go('list')} />
        ) : view === 'list' ? (
          <ShoppingList />
        ) : view === 'settings' ? (
          <Settings onDone={() => go('plan')} />
        ) : (
          view
        )}
      </main>
      {picking ? <MealPicker date={picking} onClose={() => setPicking(null)} /> : null}
    </>
  );
}

const TABS: { view: View; label: string }[] = [
  { view: 'plan', label: 'Plan' },
  { view: 'lunches', label: 'Lunches' },
  { view: 'staples', label: 'Staples' },
  { view: 'store', label: 'Shop' },
  // Without this, a reload in a supermarket lands on the planner and the only
  // way back to a half-ticked list is Shop -> store -> cupboard check again.
  { view: 'list', label: 'List' },
  { view: 'settings', label: 'Settings' },
];

function TabBar({ view, go }: { view: View; go: (v: View) => void }): React.JSX.Element {
  return (
    <nav className="tabs" aria-label="Sections">
      {TABS.map((tab) => (
        <button
          key={tab.view}
          type="button"
          aria-current={view === tab.view ? 'page' : undefined}
          onClick={() => go(tab.view)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}

function WeekStepper({ shiftWeek }: { shiftWeek: (weeks: number) => void }): React.JSX.Element {
  return (
    <div className="week-step">
      <button type="button" aria-label="Previous week" onClick={() => shiftWeek(-1)}>Earlier</button>
      <button type="button" aria-label="Next week" onClick={() => shiftWeek(1)}>Later</button>
    </div>
  );
}
